/**
 * Custom hook for managing agent UI interfaces
 * Handles fetching, CRUD operations, and AI schema generation state
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import type { AgentUiRecord } from "../types/agent-ui-types";
import * as agentUiService from "../services/agent-ui-service";

/** Hook state for the agent UIs list */
interface UseAgentUisState {
  uis: AgentUiRecord[];
  isLoading: boolean;
  error: string | null;
}

/** Return type for the useAgentUis hook */
interface UseAgentUisReturn {
  uis: AgentUiRecord[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  deleteUi: (uiId: string) => Promise<boolean>;
  togglePublish: (uiId: string) => Promise<boolean>;
  setDefault: (uiId: string) => Promise<boolean>;
}

/**
 * Hook for fetching and managing the list of agent UI interfaces
 */
export function useAgentUis(agentId: string): UseAgentUisReturn {
  const [state, setState] = useState<UseAgentUisState>({
    uis: [],
    isLoading: true,
    error: null,
  });

  const fetchUis = useCallback(async () => {
    if (!agentId || agentId === "new") return;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const uis = await agentUiService.getAgentUis(agentId);
      setState({ uis, isLoading: false, error: null });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load interfaces";
      setState((prev) => ({ ...prev, isLoading: false, error: message }));
      toast.error("Failed to load interfaces");
    }
  }, [agentId]);

  useEffect(() => {
    fetchUis();
  }, [fetchUis]);

  const deleteUi = useCallback(
    async (uiId: string): Promise<boolean> => {
      try {
        await agentUiService.deleteAgentUi(agentId, uiId);
        setState((prev) => ({
          ...prev,
          uis: prev.uis.filter((ui) => ui.id !== uiId),
        }));
        toast.success("Interface deleted");
        return true;
      } catch {
        toast.error("Failed to delete interface");
        return false;
      }
    },
    [agentId]
  );

  const togglePublishFn = useCallback(
    async (uiId: string): Promise<boolean> => {
      try {
        const updated = await agentUiService.togglePublish(agentId, uiId);
        setState((prev) => ({
          ...prev,
          uis: prev.uis.map((ui) => (ui.id === uiId ? updated : ui)),
        }));
        toast.success(
          updated.published ? "Interface published" : "Interface unpublished"
        );
        return true;
      } catch {
        toast.error("Failed to toggle publish status");
        return false;
      }
    },
    [agentId]
  );

  const setDefaultFn = useCallback(
    async (uiId: string): Promise<boolean> => {
      try {
        const updated = await agentUiService.setDefaultUi(agentId, uiId);
        setState((prev) => ({
          ...prev,
          uis: prev.uis.map((ui) => ({
            ...ui,
            isDefault: ui.id === uiId ? updated.isDefault : false,
          })),
        }));
        toast.success("Default interface updated");
        return true;
      } catch {
        toast.error("Failed to set default interface");
        return false;
      }
    },
    [agentId]
  );

  return {
    uis: state.uis,
    isLoading: state.isLoading,
    error: state.error,
    refetch: fetchUis,
    deleteUi,
    togglePublish: togglePublishFn,
    setDefault: setDefaultFn,
  };
}
